import {
  Download,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Table as TableIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { BASE as PAYROLL_BASE } from '../api';
import { PAYROLL_STATUSES, payrollStatusLabel, payrollStatusVariant } from '../constants';
import { useRuns } from '../queries';
import { usePayrollUi, type PayrollViewMode } from '../store/viewPrefs';
import type { PayrollRun } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type RunOrderingColumn =
  | 'period_start'
  | 'period_end'
  | 'status'
  | 'finalized_at'
  | 'created'
  | 'modified';

const VIEW_MODES: readonly ViewModeOption<PayrollViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

function periodLabel(run: PayrollRun): string {
  return `${formatDate(run.period_start)} – ${formatDate(run.period_end)}`;
}

export function PayrollListPage() {
  const navigate = useNavigate();
  const view = usePayrollUi((s) => s.view);
  const setView = usePayrollUi((s) => s.setView);
  const ordering = usePayrollUi((s) => s.ordering);
  const setOrdering = usePayrollUi((s) => s.setOrdering);

  // Server-driven filters live in local state — pagination + status hit the API.
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useRuns({
    page,
    status: status === 'all' ? undefined : status,
    ordering,
  });

  const results = data?.results ?? [];
  const open = (r: PayrollRun) => navigate(`/finance/payroll/${r.public_id}`);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Payroll</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Payroll runs — batch staff payslips for a pay period, built from their signed staff
            invoices.
          </p>
        </div>
        <Button onClick={() => navigate('/finance/payroll/new')}>
          <Plus /> New run
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {PAYROLL_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `payroll-runs.csv` without leaving the SPA.
              void exportCsv(PAYROLL_BASE, {
                status: status === 'all' ? undefined : status,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export payroll runs as CSV"
            title="Export payroll runs as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <RunsSkeleton />
      ) : results.length === 0 ? (
        <RunsTable runs={[]} onRowClick={() => undefined} ordering={ordering} onOrderingChange={setOrdering} />
      ) : view === 'table' ? (
        <RunsTable runs={results} onRowClick={open} ordering={ordering} onOrderingChange={setOrdering} />
      ) : view === 'list' ? (
        <RunsList runs={results} onOpen={open} />
      ) : view === 'cards' ? (
        <RunsCards runs={results} onOpen={open} />
      ) : (
        <RunsGroupedByStatus runs={results} onOpen={open} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'run' : 'runs'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  runs: PayrollRun[];
  onOpen: (r: PayrollRun) => void;
}

interface RunsTableProps {
  runs: PayrollRun[];
  onRowClick: (r: PayrollRun) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function RunsTable({ runs, onRowClick, ordering, onOrderingChange }: RunsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<RunOrderingColumn>
              column="period_start"
              value={ordering}
              onChange={onOrderingChange}
            >
              Period
            </SortableTableHead>
            <SortableTableHead<RunOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<RunOrderingColumn>
              column="finalized_at"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Finalized
            </SortableTableHead>
            <TableHead className="hidden md:table-cell">Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {runs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-10 text-center">
                No payroll runs found.
              </TableCell>
            </TableRow>
          ) : (
            runs.map((run) => (
              <TableRow key={run.id} className="cursor-pointer" onClick={() => onRowClick(run)}>
                <TableCell className="font-medium">{periodLabel(run)}</TableCell>
                <TableCell>
                  <Badge variant={payrollStatusVariant(run.status)}>
                    {payrollStatusLabel(run.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {formatDate(run.finalized_at)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {run.note || '—'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function RunsCards({ runs, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {runs.map((run) => (
        <Card
          key={run.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(run)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(run);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{periodLabel(run)}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={payrollStatusVariant(run.status)}>
              {payrollStatusLabel(run.status)}
            </Badge>
            {run.note ? <span className="text-muted-foreground truncate">{run.note}</span> : null}
            <span className="text-muted-foreground ml-auto">{formatDate(run.finalized_at)}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RunsList({ runs, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {runs.map((run) => (
        <li key={run.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(run)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">{periodLabel(run)}</span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {run.note || '—'}
            </span>
            <Badge variant={payrollStatusVariant(run.status)} className="shrink-0">
              {payrollStatusLabel(run.status)}
            </Badge>
            <span className="text-muted-foreground text-xs">{formatDate(run.finalized_at)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function RunsGroupedByStatus({ runs, onOpen }: ListProps) {
  // Bucket by status, preserving the declared status order; skip empty groups.
  const groups = useMemo(() => {
    return PAYROLL_STATUSES.map((s) => ({
      key: s.value,
      label: s.label,
      bucket: runs.filter((r) => r.status === s.value),
    }));
  }, [runs]);

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`payroll-status-${key}`}>
            <h2
              id={`payroll-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <RunsCards runs={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function RunsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
